import React, { useState } from "react";
import { ChevronDownIcon, ChevronUpIcon } from "@heroicons/react/24/outline";
import MedicationCreate from "../service-steps/clinical-assessment/MedicationCreate";
import DiscontinueDialog from "../medications/DiscontinueDialog";

import {
  formatDateToDD_MMM_YYYY,
  pickFirstValidServiceDate,
} from "../../../../helpers/functions";
import { useParams } from "react-router-dom";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { updateMedication } from "../../../../redux/modules/clinical-services/actions";
import { setSelectedMedicationId } from "../../../../redux/modules/medications/actions";

const VaccineTable = ({
  headerName,
  records,
  showHeader = true,
}) => {
  const { id: patientId } = useParams();
  const dispatch = useDispatch();
  const { selectedMedicationId } = useSelector((state) => state.medications || {});


  // Medication create state
  const [showMedicationSlider, setShowMedicationSlider] = useState(false);
  const [clickedMedication, setClickedMedication] = useState(null);

  // Discontinue dialog state
  const [openDiscontinuePopup, setOpenDiscontinuePopup] = useState(false);
  const [discontinueDrug, setDiscontinueDrug] = useState(null);

  // Action menu modal state
  const [showActionMenu, setShowActionMenu] = useState(false);
  const [actionMenuPosition, setActionMenuPosition] = useState({ x: 0, y: 0 });
  const [selectedMedicationForAction, setSelectedMedicationForAction] = useState(null);
  const [openActionMenuId, setOpenActionMenuId] = useState(null);

  const handleActionMenuOpen = (medication, event) => {
    const rect = event.currentTarget.getBoundingClientRect();
    const menuHeight = 200; // Approximate height of the menu
    const viewportHeight = window.innerHeight;
    const spaceBelow = viewportHeight - rect.bottom;
    const spaceAbove = rect.top;

    // Determine if we should open above or below
    const openAbove = spaceBelow < menuHeight && spaceAbove > menuHeight;

    setActionMenuPosition({
      x: rect.right - 200, // Position menu to the left of the button
      y: openAbove ? rect.top - menuHeight - 5 : rect.bottom + 5
    });
    setSelectedMedicationForAction(medication);
    setOpenActionMenuId(medication.id);
    setShowActionMenu(true);
  };

  const handleActionMenuClose = () => {
    setShowActionMenu(false);
    setSelectedMedicationForAction(null);
    setOpenActionMenuId(null);
  };

  const updateMedicationStatus = (medication, status, discontinueData = null) => {
    const updatedMedicationObject = {
      ...medication,
      status: status,
      ...(discontinueData && {
        inactiveDate: discontinueData.inactiveDate,
        inactiveWhen: discontinueData.whenDiscontinued,
        inactiveInitiator: discontinueData.source,
        inactiveCommunicationType: discontinueData.communicationType,
        inactiveReason: discontinueData.reason,
        inactiveOtherReason: discontinueData.otherReason,
      }),
    };
    let medicationData = {
      medications: [updatedMedicationObject],
    };
    dispatch(updateMedication(patientId, medicationData));
  };

  const activeRecords = (records || []).filter((med) => med.status === "active");

  return (
    <>
      <div className="flex flex-col gap-3">
        {showHeader && (
          <div className="flex justify-between items-center pb-[10px]">
            <div className="flex gap-3 items-center">
              <h4 className="text-xl font-semibold text-teal-300 px-[5px]">{headerName}</h4>
            </div>
          </div>
        )}
        <div className="rounded-[4px] border border-gray-100 overflow-x-auto">
          <div className="max-h-[360px] overflow-y-auto">
            <table className="min-w-full">
              <colgroup>
                <col className="w-[110px] h-[44px]" />
                <col className="w-[50px] h-[44px]" />
                <col className="w-[360px] h-[44px]" />
                <col className="w-[120px] h-[44px]" />
                <col className="w-[160px] h-[44px]" />
                <col className="w-[611px] h-[44px]" />
              </colgroup>
              <thead className="sticky top-0 z-10 bg-white">
                <tr className="h-[52px] bg-gray-20 text-base font-medium text-left text-gray-800 px-[10px] py-[10px]">
                  <th scope="col" className="pl-6">Status</th>
                  <th scope="col" className="text-center"></th>
                  <th scope="col" className="pl-2 border-l-[1px] border-gray-100">Vaccine Name</th>
                  <th scope="col" className="pl-2 border-l-[1px] border-gray-100">Service Date</th>
                  <th scope="col" className="pl-2 border-l-[1px] border-gray-100">Vaccine Category</th>
                  <th scope="col" className="pl-2 border-l-[1px] border-gray-100">Prescription Details</th>
                </tr>
              </thead>
              <tbody className="min-w-full bg-white text-sm text-gray-800 gap-4">
                {activeRecords.map((med, i) => {
                  const isEvenRow = i % 2 === 0;
                  const isSelected = String(selectedMedicationId) === String(med.id);
                  const serviceDateValue = pickFirstValidServiceDate(
                    med.doseDate,
                    med.startDate,
                    med.created,
                    med.createdAt
                  );
                  return (
                    <tr
                      className={`min-h-[44px] ${med.status === "inactive"
                        ? "bg-red-50"
                        : isEvenRow
                          ? "bg-white"
                          : "bg-gray-20"
                        } ${isSelected ? "!bg-blue-100" : ""} transition-colors duration-300 cursor-pointer group hover:bg-gray-50`}
                      key={med.id}
                      onClick={() => {
                        dispatch(setSelectedMedicationId(med.id));
                      }}
                    >
                      {/* Status */}
                      <td className="border-r-2 border-gray-100 justify-start py-[10px] px-5 gap-2">
                        <div className={`bg-transparent  rounded-y-[4px] rounded-l-[4px] p-[10px] flex items-center justify-start ${med.status === "inactive" ? "border-red-200" : "border-green-200"}`}>
                          <div className={`w-[14px] h-[14px] rounded-full ${med.status === "inactive" ? "bg-red-200" : "bg-green-200"}`}>
                          </div>
                        </div>
                      </td>

                      <td className="border-r-[1px] border-gray-100 py-[10px] pr-[20px] pl-[20px] gap-2 w-[56px] text-center">
                        {/* Action Button with Chevron */}
                        <button
                          className={`rounded-y-[4px] rounded-r-[4px] flex items-center justify-center transition-colors `}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleActionMenuOpen(med, e);
                          }}
                        >
                          {openActionMenuId === med.id ? (
                            <ChevronUpIcon className="w-6 h-6 stroke-1 stroke-gray-800" />
                          ) : (
                            <ChevronDownIcon className="w-6 h-6 stroke-1 stroke-gray-800" />
                          )}
                        </button>
                      </td>

                      {/* Vaccine Name */}
                      <td className="flex flex-col py-[10px] pl-[10px] pr-[50px] text-gray-800 text-sm font-normal align-top">
                        <span className="capitalize">
                          {med.manufacturerCode ? `${med.manufacturerCode} - ${med.medicationName}` : med.medicationName}
                        </span>
                        <span>
                          {med.manufacturerName}
                        </span>
                      </td>

                      {/* Service Date */}
                      <td className="py-[10px] pr-[10px] border-l-[1px] border-gray-100 pl-[10px] text-gray-800 text-sm font-normal">
                        {serviceDateValue ? formatDateToDD_MMM_YYYY(new Date(serviceDateValue)) : "N/A"}
                      </td>

                      {/* Vaccine Category (empty for now) */}
                      <td className="py-[10px] border-l-2 border-gray-100 px-[10px] text-gray-800 text-sm font-normal">
                        <span>{med.vaccineCategory}</span>
                      </td>

                      {/* Prescription Details (moved from Vaccine Category) */}
                      <td className="py-[10px] border-l-[1px] border-gray-100 pr-[12px] pl-[10px]">
                        <div className="flex items-start justify-between">
                          <div className="flex-1 pr-[36px]">
                            {(() => {
                              const labelFromOption = (val) => {
                                if (val == null || val === "") return null;
                                if (typeof val === "object")
                                  return val.value ?? val.label ?? null;
                                return String(val);
                              };

                              const details = [];
                              if (med.medicationName) details.push(med.medicationName);
                              if (med.sig) details.push(med.sig);
                              if (med.comments) details.push(med.comments);
                              const lineOne = details.length ? details.join(", ") : "N/A";

                              const doseParts = [];
                              if (med.vaccineDoseType)
                                doseParts.push(String(med.vaccineDoseType));
                              if (
                                med.totalNumberOfVaccineDoses != null &&
                                med.totalNumberOfVaccineDoses !== ""
                              ) {
                                doseParts.push(
                                  `Total doses: ${med.totalNumberOfVaccineDoses}`
                                );
                              }
                              const qtyRaw = med.dispenseQuantity ?? med.quantity;
                              const qtyLabel = labelFromOption(qtyRaw);
                              if (qtyLabel) doseParts.push(`Doses given: ${qtyLabel}`);
                              const dosageLabel = labelFromOption(med.dosage);
                              const formLabel = labelFromOption(med.form);
                              if (dosageLabel && formLabel)
                                doseParts.push(`${dosageLabel} ${formLabel}`);
                              else if (dosageLabel) doseParts.push(dosageLabel);
                              else if (formLabel) doseParts.push(formLabel);
                              const routeLabel = labelFromOption(med.route);
                              if (routeLabel) doseParts.push(routeLabel);

                              const lineTwo = doseParts.length ? doseParts.join(", ") : "";

                              return (
                                <span>
                                  {lineOne}
                                  {lineTwo ? (
                                    <>
                                      <br />
                                      {lineTwo}
                                    </>
                                  ) : null}
                                </span>
                              );
                            })()}
                          </div>
                          <button
                            className="p-[6px] mt-[2px]"
                            onClick={(e) => {
                              e.stopPropagation();
                              setOpenActionMenuId((prev) => (prev === `rx-${med.id}` ? null : `rx-${med.id}`));
                            }}
                          >
                            {openActionMenuId === `rx-${med.id}` ? (
                              <ChevronUpIcon className="w-6 h-6 stroke-1 stroke-gray-800" />
                            ) : (
                              <ChevronDownIcon className="w-6 h-6 stroke-1 stroke-gray-800" />
                            )}
                          </button>
                        </div>
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          {activeRecords.length === 0 && (
            <div className="p-8 text-center text-gray-500">
              <p>No vaccines found</p>
            </div>
          )}
        </div>


        {/* Custom Action Menu Modal */}
        {showActionMenu && selectedMedicationForAction && (
          <div
            className="fixed inset-0 z-50 ml-48"
            onClick={handleActionMenuClose}
          >
            <div
              className="flex flex-col absolute bg-white shadow-lg border border-gray-200 rounded-[4px] w-28"
              style={{
                left: `${actionMenuPosition.x}px`,
                top: `${actionMenuPosition.y}px`,
              }}
              onClick={(e) => e.stopPropagation()}
            >
              <button
                className={`${selectedMedicationForAction?.status === "inactive" &&
                  "opacity-40 pointer-events-none text-gray-200"
                  } flex w-full text-left text-sm font-normal text-gray-800 pl-[14px] pr-7 py-[10px] border-b-[0.5px] border-b-gray-50 hover:bg-gray-50`}
                onClick={() => {
                  setOpenDiscontinuePopup(true);
                  setDiscontinueDrug(selectedMedicationForAction);
                  handleActionMenuClose();
                }}
              >
                Discontinue
              </button>
              <button
                disabled={true}
                className="opacity-40 pointer-events-none flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[10px] border-b-[0.5px] border-b-gray-50 hover:bg-gray-50"
              >
                History
              </button>
              <button
                className="opacity-40 flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[10px] border-b-[0.5px] border-b-gray-50 hover:bg-gray-50"
                disabled={true}
              >
                Hold
              </button>
              <button
                className="flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[10px] border-b-[0.5px] border-b-gray-50 hover:bg-gray-50"
                onClick={() => {
                  setClickedMedication(selectedMedicationForAction);
                  setShowMedicationSlider(true);
                  handleActionMenuClose();
                }}
              >
                Modify
              </button>
              <button
                disabled={selectedMedicationForAction?.status === "active"}
                className={` ${selectedMedicationForAction?.status === "active" &&
                  "opacity-40 pointer-events-none"
                  } flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[10px] border-b-[0.5px] border-b-gray-50 hover:bg-gray-50`}
                onClick={() => {
                  // Void functionality
                  handleActionMenuClose();
                }}
              >
                Void
              </button>
              <button
                disabled={selectedMedicationForAction?.status === "active"}
                className={` ${selectedMedicationForAction?.status === "active" &&
                  "opacity-40 pointer-events-none"
                  } flex w-full text-left text-sm font-normal text-gray-800 px-[14px] py-[10px] border-b-[0.5px] border-b-gray-50 hover:bg-gray-50`}
                onClick={() => {
                  updateMedicationStatus(selectedMedicationForAction, "active");
                  handleActionMenuClose();
                }}
              >
                Resume
              </button>
            </div>
          </div>
        )}

        {/* Medication Create Modal */}
        <MedicationCreate
          create={clickedMedication?.id ? false : true}
          patientId={patientId}
          type="Medication"
          selectedDrugType="Vaccine"
          title="Vaccine"
          open={showMedicationSlider}
          fullMedication={clickedMedication}
          step={1}
          maxSteps={1}
          onClose={(value) => {
            setClickedMedication(null);
            setShowMedicationSlider(value);
          }}
          onSave={() => {
            setShowMedicationSlider(false);
          }}
        />

        {/* Discontinue Dialog */}
        <DiscontinueDialog
          open={openDiscontinuePopup}
          drug={discontinueDrug}
          onCross={() => {
            setOpenDiscontinuePopup(false);
            setDiscontinueDrug(null);
          }}
          handleClose={() => {
            setOpenDiscontinuePopup(false);
            setDiscontinueDrug(null);
          }}
          confirmAction={(discontinueData) => {
            updateMedicationStatus(discontinueDrug, "inactive", discontinueData);
            setOpenDiscontinuePopup(false);
            setDiscontinueDrug(null);
          }}
        />
      </div>
    </>
  );
};

export default VaccineTable;
